import { Loader2 } from 'lucide-react';
import React, { type FormEvent, useCallback, useRef, useState } from 'react';

import {
    ConversationRecipientHistory,
    useConversationRecipientPreview,
} from '@apps/web/components/shared/messages/conversation_recipient_history';
import {
    type ConversationRecipient,
    ConversationRecipientPicker,
} from '@apps/web/components/shared/messages/conversation_recipient_picker';
import { createConversationMessage } from '@apps/web/components/shared/messages/create_conversation_message';
import { EmojiEnabledTextarea } from '@apps/web/components/shared/messages/emoji_composer_support';
import {
    MESSAGE_INPUT_LIMIT_ERROR,
    MESSAGE_MAX_LENGTH,
} from '@apps/web/components/shared/messages/message_composer_constants';
import {
    messageAndImagesFitMms,
    MessageImageUploadControls,
    useMessageImageUploads,
} from '@apps/web/components/shared/messages/message_image_uploads';
import {
    fetchOutboundTranslationPreview,
    TranslationPreviewDialog,
} from '@apps/web/components/shared/messages/translation_preview_dialog';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { toast } from '@apps/web/lib/app_notifications';
import { ApiError } from '@libraries/typescript/api_client';

type NewConversationMessageDialogProps = {
    readonly organizationId: string;
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly onMessageCreated: (conversationId: string) => void | Promise<void>;
};

export function NewConversationMessageDialog({
    organizationId,
    open,
    onOpenChange,
    onMessageCreated,
}: NewConversationMessageDialogProps) {
    const { timezone } = useOrganizationTimezone();
    const [search, setSearch] = useState('');
    const [recipient, setRecipient] = useState<ConversationRecipient | null>(
        null,
    );
    const preview = useConversationRecipientPreview(organizationId, recipient);
    const [content, setContent] = useState('');
    const [composerInputError, setComposerInputError] = useState<string | null>(
        null,
    );
    const [isSending, setIsSending] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [translationPreview, setTranslationPreview] = useState<{
        readonly englishSource: string;
        readonly languageName: string | null;
        readonly translatedText: string;
    } | null>(null);
    // Bumped whenever the dialog resets/closes so an in-flight preview fetch
    // that resolves afterward is discarded instead of reopening the dialog.
    const previewGenerationRef = useRef(0);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const {
        uploads,
        attachmentIds,
        isUploading,
        hasFailedUploads,
        totalSizeBytes,
        addFiles,
        clear: clearImages,
        handlePaste,
        removeUpload,
    } = useMessageImageUploads(organizationId);
    const handleEmojiInsertionLimit = useCallback(() => {
        setComposerInputError(MESSAGE_INPUT_LIMIT_ERROR);
    }, []);
    const selectedCaregiver =
        recipient?.kind === 'caregiver' ? recipient.caregiver : null;
    const trimmedContent = content.trim();
    // Locked from the moment Send is clicked through preview + send, so the
    // recipient and draft can't change out from under an in-flight
    // translate-and-send (which captured the original caregiver and text).
    const isBusy =
        isSending || isPreviewing || isUploading || translationPreview !== null;
    const canSend =
        preview.canSend &&
        (trimmedContent.length > 0 || attachmentIds.length > 0) &&
        !hasFailedUploads &&
        !isBusy;

    const resetState = () => {
        previewGenerationRef.current += 1;
        setSearch('');
        setRecipient(null);
        setContent('');
        setComposerInputError(null);
        setIsSending(false);
        setIsPreviewing(false);
        setTranslationPreview(null);
        clearImages();
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (isSending) {
            return;
        }

        if (!nextOpen) {
            resetState();
        }
        onOpenChange(nextOpen);
    };

    const doCreate = async (body: string, englishSource: string | null) => {
        if (recipient === null) {
            // Nothing to send (e.g. the selection was lost) — release the lock
            // so the composer doesn't get stuck showing a pending spinner.
            setIsPreviewing(false);
            return;
        }
        if (!messageAndImagesFitMms(body, totalSizeBytes)) {
            toast.error('Message and images must be 5 MB or smaller', {
                persistNotification: false,
            });
            setIsPreviewing(false);
            return;
        }

        setIsSending(true);
        let conversationId: string;
        try {
            conversationId = await createConversationMessage({
                organizationId,
                recipient,
                content: body,
                englishSource,
                attachmentIds,
            });
        } catch (error) {
            console.error('Failed to send conversation message', error);
            toast.error(
                error instanceof ApiError && error.message.trim() !== ''
                    ? error.message
                    : 'Failed to send message',
                {
                    persistNotification: false,
                },
            );
            setIsSending(false);
            setIsPreviewing(false);
            return;
        }

        toast.success('Message sent', { persistNotification: false });
        resetState();
        onOpenChange(false);

        try {
            await onMessageCreated(conversationId);
        } catch (error) {
            console.error('Failed to open sent conversation message', error);
            toast.error('Message sent, but failed to open conversation', {
                persistNotification: false,
            });
        }
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!canSend || translationPreview !== null) {
            return;
        }

        if (recipient?.kind === 'phoneNumber' || recipient?.kind === 'client') {
            await doCreate(trimmedContent, null);
            return;
        }

        if (!selectedCaregiver) {
            return;
        }

        if (!trimmedContent) {
            await doCreate('', null);
            return;
        }

        // Translate for the recipient first; fall through to the original
        // English on any error (today's behavior). The preview fetch uses its
        // own pending state so the dialog stays dismissable during the call.
        const generation = (previewGenerationRef.current += 1);
        setIsPreviewing(true);
        let preview = null;
        try {
            preview = await fetchOutboundTranslationPreview({
                organizationId,
                content: trimmedContent,
                caregiverId: selectedCaregiver.id,
            });
        } catch (error) {
            console.error('Failed to translate message preview', error);
        }

        // Discard the result if the dialog was closed/reset mid-fetch.
        if (previewGenerationRef.current !== generation) {
            return;
        }

        if (preview?.translated_text && preview.target_language_code) {
            setIsPreviewing(false);
            setTranslationPreview({
                englishSource: trimmedContent,
                languageName: preview.target_language_name,
                translatedText: preview.translated_text,
            });
            return;
        }

        // No translation needed: hand off to the send without releasing the
        // lock, so the dialog can't be dismissed between preview and send.
        await doCreate(trimmedContent, null);
    };

    return (
        <React.Fragment>
            <Dialog onOpenChange={handleOpenChange} open={open}>
                <DialogContent className="sm:max-w-xl">
                    <form
                        className="space-y-5"
                        onPaste={handlePaste}
                        onSubmit={handleSubmit}
                    >
                        <DialogHeader>
                            <DialogTitle>New message</DialogTitle>
                            <DialogDescription className="sr-only">
                                Start a text conversation from the shared inbox.
                            </DialogDescription>
                        </DialogHeader>
                        <ConversationRecipientPicker
                            disabled={isBusy}
                            onRecipientChange={setRecipient}
                            onSearchChange={setSearch}
                            organizationId={organizationId}
                            recipient={recipient}
                            search={search}
                            searchReason="new-conversation-message-dialog"
                            variant="stacked"
                        />
                        {preview.enabled ? (
                            <div className="flex h-56 min-h-0 flex-col overflow-hidden rounded-lg border">
                                <ConversationRecipientHistory
                                    organizationId={organizationId}
                                    timezone={timezone}
                                    preview={preview}
                                />
                            </div>
                        ) : null}
                        <EmojiEnabledTextarea
                            className="relative"
                            content={content}
                            maxLength={MESSAGE_MAX_LENGTH}
                            onContentChange={setContent}
                            onInsertionLimit={handleEmojiInsertionLimit}
                            onInteraction={() => {
                                setComposerInputError(null);
                            }}
                            pickerDisabled={isBusy}
                            resetKey={open}
                            textareaProps={{
                                className: 'pr-12',
                                disabled: isBusy,
                                placeholder: 'Write a message…',
                                rows: 5,
                            }}
                            textareaRef={textareaRef}
                        >
                            {({ emojiPicker, textarea }) => (
                                <React.Fragment>
                                    {textarea}
                                    <div className="absolute right-2 bottom-2">
                                        {emojiPicker}
                                    </div>
                                </React.Fragment>
                            )}
                        </EmojiEnabledTextarea>
                        {composerInputError ? (
                            <p className="text-destructive -mt-3 text-xs">
                                {composerInputError}
                            </p>
                        ) : null}
                        <MessageImageUploadControls
                            disabled={isBusy}
                            onAddFiles={addFiles}
                            onRemove={removeUpload}
                            uploads={uploads}
                        />
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button
                                disabled={isSending}
                                onClick={() => {
                                    handleOpenChange(false);
                                }}
                                type="button"
                                variant="outline"
                            >
                                Cancel
                            </Button>
                            <Button disabled={!canSend} type="submit">
                                {isSending || isPreviewing ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : null}
                                Send message
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
            {translationPreview !== null ? (
                <TranslationPreviewDialog
                    englishSource={translationPreview.englishSource}
                    isSending={isSending}
                    languageName={translationPreview.languageName}
                    onCancel={() => {
                        setTranslationPreview(null);
                    }}
                    onSendEnglish={() => {
                        void doCreate(translationPreview.englishSource, null);
                    }}
                    onSendTranslated={(translatedBody) => {
                        void doCreate(
                            translatedBody,
                            translationPreview.englishSource,
                        );
                    }}
                    open={true}
                    translatedText={translationPreview.translatedText}
                />
            ) : null}
        </React.Fragment>
    );
}
